import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Status / state / priority pill. Two visual variants:
 *  - `soft`  : tinted background + matching foreground (order-stage chips).
 *  - `solid` : solid background + white text (live order-board & cooking state,
 *              priority flags).
 * The pill is style-only — pass the (i18n) label as children so callers own the
 * text. Backed by the fixed status/order design tokens, identical in light/dark.
 */
const statusPillVariants = cva(
  "inline-flex items-center justify-center rounded-full whitespace-nowrap font-medium",
  {
    variants: {
      variant: {
        soft: "",
        solid: "text-(--brand-white)",
      },
      size: {
        sm: "px-2.5 py-0.5 text-xs",
        md: "px-3 py-1 text-sm",
        lg: "h-9 px-3 text-sm",
      },
      status: {
        cooking: "",
        plating: "",
        pickup: "",
        completed: "",
        active: "",
        ready: "",
        enroute: "",
        done: "",
        late: "",
        paused: "",
        high: "",
        normal: "",
        low: "",
      },
    },
    compoundVariants: [
      // soft — order-stage tinted pills
      { variant: "soft", status: "cooking", class: "bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg)" },
      { variant: "soft", status: "plating", class: "bg-(--color-order-plating-bg) text-(--color-order-plating-fg)" },
      { variant: "soft", status: "pickup", class: "bg-(--color-order-pickup-bg) text-(--color-order-pickup-fg)" },
      { variant: "soft", status: "completed", class: "bg-(--color-order-completed-bg) text-(--color-order-completed-fg)" },
      { variant: "soft", status: "active", class: "bg-(--color-success-bg) text-(--color-success-text)" },
      { variant: "soft", status: "late", class: "bg-(--color-danger-bg) text-(--color-danger-text)" },
      { variant: "soft", status: "paused", class: "bg-(--color-warning-bg) text-(--color-warning-text)" },
      // solid — live state + priority. Solid fill in light; in dark it shifts to
      // a soft tint + coloured text + hairline border (per the dark design,
      // where bright solid fills glare on black).
      { variant: "solid", status: "cooking", class: "bg-(--brand-blue) dark:bg-(--brand-blue)/15 dark:text-(--brand-blue) dark:border dark:border-(--brand-blue)/30" },
      { variant: "solid", status: "late", class: "bg-(--color-danger) dark:bg-(--color-danger)/15 dark:text-(--color-danger) dark:border dark:border-(--color-danger)/30" },
      { variant: "solid", status: "paused", class: "bg-(--color-warning) dark:bg-(--color-warning)/15 dark:text-(--color-warning) dark:border dark:border-(--color-warning)/30" },
      { variant: "solid", status: "plating", class: "bg-(--color-order-plating-fg) dark:bg-(--color-order-plating-fg)/15 dark:text-(--color-order-plating-fg) dark:border dark:border-(--color-order-plating-fg)/30" },
      { variant: "solid", status: "ready", class: "bg-(--color-status-active) dark:bg-(--color-status-active)/15 dark:text-(--color-status-active) dark:border dark:border-(--color-status-active)/30" },
      { variant: "solid", status: "enroute", class: "bg-(--color-warning) dark:bg-(--color-warning)/15 dark:text-(--color-warning) dark:border dark:border-(--color-warning)/30" },
      { variant: "solid", status: "done", class: "bg-(--gray-400) dark:bg-(--gray-400)/15 dark:text-(--gray-400) dark:border dark:border-(--gray-400)/30" },
      { variant: "solid", status: "active", class: "bg-(--color-status-active) dark:bg-(--color-status-active)/15 dark:text-(--color-status-active) dark:border dark:border-(--color-status-active)/30" },
      { variant: "solid", status: "high", class: "bg-(--color-priority-high) dark:bg-(--color-priority-high)/15 dark:text-(--color-priority-high) dark:border dark:border-(--color-priority-high)/30" },
      { variant: "solid", status: "normal", class: "bg-(--color-warning) dark:bg-(--color-warning)/15 dark:text-(--color-warning) dark:border dark:border-(--color-warning)/30" },
      { variant: "solid", status: "low", class: "bg-(--gray-400) dark:bg-(--gray-400)/15 dark:text-(--gray-400) dark:border dark:border-(--gray-400)/30" },
    ],
    defaultVariants: { variant: "soft", status: "cooking", size: "md" },
  },
);

export interface StatusPillProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusPillVariants> {}

export function StatusPill({
  className,
  variant,
  status,
  size,
  ...props
}: StatusPillProps) {
  return (
    <span
      className={cn(statusPillVariants({ variant, status, size }), className)}
      {...props}
    />
  );
}

export { statusPillVariants };
